import { useCallback } from "react";
import {
  buildMarkersFromLocations,
  createOrUpdateMarkerCluster,
  clearMarkers,
} from "../utils/markers";
import { Location, SelectedFilters, BrandList } from "../types/mapData";
import { sortLocations } from "../utils/sortLocations";

interface UseLocationManagerProps {
  map: google.maps.Map | null;
  oval: string;
  showSearchBox: boolean;
  showSidebar: boolean;
  showZoombox: boolean;
  showPassPopup: boolean;
  userLocation: any;
  staticLocation: any;
  curLoc: Location | null;
  curMarker: google.maps.Marker | null;
  markersState: google.maps.Marker[];
  locationsRef: React.MutableRefObject<Location[]>;
  curMarkerRef: React.MutableRefObject<google.maps.Marker | null>;
  listStateRef: React.MutableRefObject<number>;
  setMarkersState: (markers: google.maps.Marker[]) => void;
  setLocations: (locations: Location[]) => void;
  setFilteredLocations: (locations: Location[]) => void;
  setCurBrand: (brandId: number) => void;
  setCurLoc: (location: Location | null) => void;
  setListState: (state: number) => void;
  setInfoOpenMobile: (open: boolean) => void;
  handleCurMarker: (marker: google.maps.Marker | null) => void;
  handleAmplitude: (eventName: string, props?: Record<string, any>) => void;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  brands: BrandList | null;
  initSelectedTag: SelectedFilters | null;
  selectedCuisines: SelectedFilters | null;
}

export function useLocationManager({
  map,
  oval,
  showSearchBox,
  showSidebar,
  showZoombox,
  showPassPopup,
  userLocation,
  staticLocation,
  curLoc,
  curMarker,
  markersState,
  locationsRef,
  curMarkerRef,
  listStateRef,
  setMarkersState,
  setLocations,
  setFilteredLocations,
  setCurBrand,
  setCurLoc,
  setListState,
  setInfoOpenMobile,
  setCurrentPage,
  handleCurMarker,
  handleAmplitude,
  initSelectedTag,
  brands,
  selectedCuisines,
}: UseLocationManagerProps) {
  // INTERNAL HELPER FUNCTIONS
  const _handleFilter = useCallback(
    (field: string, value: any, locs: Location[]) => {
      const filtered = locs.filter(
        (loc) => loc[field as keyof Location] === value,
      );
      setFilteredLocations(filtered);
    },
    [setFilteredLocations],
  );
  const _addListeners = useCallback(
    (markers: google.maps.Marker[], locs: Location[]) => {
      markers.forEach((marker) => {
        google.maps.event.clearInstanceListeners(marker);
        google.maps.event.addListener(marker, "click", () => {
          handleAmplitude("click_web_map_content");

          if (curMarkerRef.current?.locName === marker.locName) {
            setFilteredLocations(locationsRef.current);
            setListState(1);
            setCurLoc(null);
            handleCurMarker(null);
            setCurBrand(0);
          } else if (marker.isMulti === true) {
            setInfoOpenMobile(true);
            setListState(1);
            setCurLoc(null);
            handleCurMarker(marker);
            setCurBrand(0);
            _handleFilter("latlng", marker.multiLoc, locs);
          } else {
            if (listStateRef.current === 2) {
              const tile = document.getElementsByClassName("loc-tile")[0] as
                | HTMLImageElement
                | undefined;
              const logo = document.getElementsByClassName("loc-logo")[0] as
                | HTMLImageElement
                | undefined;
              if (tile) tile.src = "";
              if (logo) logo.src = "";
            }
            setCurBrand(marker.location.brand_id);
            setCurLoc(marker.location);
            handleCurMarker(marker);
            setListState(2);
            const container = document.getElementsByClassName(
              "info-container-inner",
            )[0];
            container.scrollTop = 0;
          }
        });
      });
    },
    [
      handleAmplitude,
      handleCurMarker,
      setCurLoc,
      setCurBrand,
      setListState,
      setFilteredLocations,
      setInfoOpenMobile,
      curMarkerRef,
      locationsRef,
      listStateRef,
    ],
  );
  const _getDistance = (
    lng: number,
    lat: number,
    trueCenter: google.maps.LatLng,
    isStatic: boolean,
  ) => {
    // Fallback coordinates
    const [staticLat, staticLng] = staticLocation.location ?? [40.73, -73.94];
    // Determine target coordinates
    const toLat = isStatic ? staticLat : trueCenter?.lat();
    const toLng = isStatic ? staticLng : trueCenter?.lng();

    // Create LatLng objects
    const from = new google.maps.LatLng(lat, lng);
    const to = new google.maps.LatLng(toLat, toLng);

    // Compute distance in meters, then convert to miles
    const distanceMeters =
      google.maps.geometry.spherical.computeDistanceBetween(from, to);
    const distanceMiles = distanceMeters / 1609.344;

    const rounded =
      distanceMiles > 100
        ? Math.round(distanceMiles)
        : Math.round(distanceMiles * 10) / 10;

    return rounded;
  };
  const _getTrueCenter = (
    latlng: google.maps.LatLng | string[],
    useMap: google.maps.Map,
  ) => {
    const mapProj = useMap.getProjection();
    const infoContainer = document.getElementsByClassName(
      "info-container",
    )[0] as HTMLElement;
    let offsetx =
      window.innerWidth >= 800 ? (infoContainer?.offsetWidth / 2 + 12) * -1 : 0;
    let offsety = window.innerWidth >= 800 ? 0 : -102;
    if (!showSearchBox && !showSidebar && !showZoombox && !showPassPopup) {
      offsetx = 0;
      offsety = 0;
    }
    const isInstance = latlng instanceof google.maps.LatLng;
    const point1 = mapProj?.fromLatLngToPoint(
      isInstance ? latlng : useMap?.getCenter(),
    );
    const point2 = new google.maps.Point(
      (typeof offsetx === "number" ? offsetx : 0) /
        Math.pow(2, useMap.getZoom() || 0),
      (typeof offsety === "number" ? offsety : 0) /
        Math.pow(2, useMap.getZoom() || 0),
    );
    const center = mapProj?.fromPointToLatLng(
      new google.maps.Point(point1.x - point2.x, point1.y + point2.y),
    );
    return center;
  };

  const handleLocsAndMarkers = useCallback(
    (locations: Location[], buildMarkers = true) => {
      if (!map || !locations) return;

      const trueCenter = _getTrueCenter(
        [userLocation?.location[0] ?? 40.73, userLocation?.location[1] ?? -74],
        map,
      );
      const locs = locations.map((loc) => {
        const distance = _getDistance(
          loc.location.longitude,
          loc.location.latitude,
          trueCenter,
          false,
        );
        const staticDistance = _getDistance(
          loc.location.longitude,
          loc.location.latitude,
          trueCenter,
          true,
        );

        return {
          ...loc,
          distance,
          staticDistance,
          latlng: `${loc.location.latitude}${loc.location.longitude}`,
        };
      });

      const isMarketingGuide =
        initSelectedTag?.category === "Marketing Guide" &&
        selectedCuisines?.tags?.includes(initSelectedTag?.id);
      const sortedLocs = sortLocations(
        locs,
        brands,
        isMarketingGuide ?? false,
        initSelectedTag?.id ?? 0,
      );
      setCurrentPage(1);
      if (
        curLoc &&
        userLocation?.isLoc &&
        sortedLocs[0].location_id !== curLoc.location_id
      ) {
        const index = sortedLocs.findIndex(
          (l) => l.location_id === curLoc.location_id,
        );
        if (index !== -1) {
          sortedLocs.unshift(sortedLocs.splice(index, 1)[0]);
        }
      }

      setLocations(sortedLocs);
      if (!curMarker || curMarker.isMulti !== true) {
        setFilteredLocations(sortedLocs);
      }

      if (buildMarkers) {
        clearMarkers(markersState, setMarkersState);

        const newMarkers = buildMarkersFromLocations(sortedLocs, map, oval);
        createOrUpdateMarkerCluster({
          map,
          markers: newMarkers,
          ovalUrl: oval,
        });

        if (showSearchBox) {
          _addListeners(newMarkers, sortedLocs);
        }
        setMarkersState(newMarkers);
      } else {
        const sortedMarkers = [...markersState].sort(
          (a, b) => a.location.distance - b.location.distance,
        );
        if (userLocation?.isLoc === false) {
          setListState(1);
        }
        if (showSearchBox) {
          _addListeners(sortedMarkers, sortedLocs);
        }
        setMarkersState(sortedMarkers);
      }
    },
    [
      map,
      userLocation,
      curLoc,
      curMarker,
      showSearchBox,
      showSidebar,
      showZoombox,
      showPassPopup,
      setLocations,
      setFilteredLocations,
      setMarkersState,
      setListState,
      _addListeners,
      markersState,
      oval,
      staticLocation,
      setCurrentPage,
    ],
  );

  return {
    handleLocsAndMarkers,
  };
}
